import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import {
  CourseGenerationStatus,
  GetAssignmentQuery,
  GetAxioResourceQuery,
  GetCourseByIdQuery,
  GetUserSubmissionsQuery,
  useGetAssignmentQuery,
  useGetAxioResourceQuery,
  useGetCourseByIdQuery,
  useGetUserSubmissionsQuery,
} from "../graphql/generated/graphql";
import { actions, useAppState } from "./AppStateContext";

export enum CustomCourseworkModulesViewModalState {
  CLOSED = "CLOSED",
  ADD_MODULE = "ADD_MODULE",
  EDIT_MODULE_NAME = "EDIT_MODULE_NAME",
  DELETE_MODULE_OR_ACTIVITY = "DELETE_MODULE_OR_ACTIVITY",
  PUBLISH_COURSE = "PUBLISH_COURSE",
  UNPUBLISH_COURSE = "UNPUBLISH_COURSE",
  ADD_OR_EDIT_ACTIVITY = "ADD_OR_EDIT_ACTIVITY",
  COURSE_GENERATION_DIALOG = "COURSE_GENERATION_DIALOG",
  COURSE_GENERATION_IN_PROGRESS = "COURSE_GENERATION_IN_PROGRESS",
  EDIT_IMAGE = "EDIT_IMAGE",
  EDIT_IMAGE_CONFIRMATION = "EDIT_IMAGE_CONFIRMATION",
  EDIT_IMAGE_ERROR = "EDIT_IMAGE_ERROR",
  COURSE_ROSTER = "COURSE_ROSTER",
}

export enum ResourceType {
  MODULE = "module",
  ACTIVITY_RESOURCE = "axioResource",
  ACTIVITY_ASSIGNMENT = "assignment",
  ACTIVITY_INTERACTIVE_LEARNING_EXPERIENCE = "aiTutor",
  ACTIVITY_PAGE = "activity",
  ACTIVITY_REVIEW = "review",
  ACTIVITY_ANNOUNCEMENT = "announcement",
}

export interface CourseEditPageModalState {
  state: CustomCourseworkModulesViewModalState;
  resourceType: ResourceType;
  resourceId: string;
  activeModule: {
    [key: string]: unknown;
    id: string;
    assessment?: {
      isEnabled?: boolean;
    };
  }; // Holds the module that is currently active.
  activeActivity: {
    [key: string]: unknown;
    id: string;
    type: string;
    title: string;
    description: string;
  }; // Holds the activity that is currently active.
}

type ActiveCourseContextType = {
  isSubmitting: boolean;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  courseId: string;
  setCourseId: Dispatch<SetStateAction<string>>;
  modalState: CourseEditPageModalState;
  setModalState: Dispatch<SetStateAction<CourseEditPageModalState>>;
  courseData: GetCourseByIdQuery;
  courseLoading: boolean;
  refetchCourse: () => void;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
  handleSelectModule: (module: unknown) => void;
  handleSelectActivity: (activity: unknown) => void;
  handleOpenAddModule: () => void;
  handleOpenGenerateDialog: () => void;
  handleTogglePublishDialog: () => void;
  activeResource: { resourceType: ResourceType; resourceId: string } | null;
  setActiveResource: Dispatch<SetStateAction<{ resourceType: ResourceType; resourceId: string } | null>>;
  assignmentData: GetAssignmentQuery;
  loadingAssignment: boolean;
  userSubmissionsData: GetUserSubmissionsQuery;
  loadingUserSubmissions: boolean;
  refetchUserSubmissions: () => void;
  axioResourceData: GetAxioResourceQuery;
};

const ActiveCourseContext = createContext<ActiveCourseContextType>({
  isSubmitting: false,
  setIsSubmitting: () => {},
  courseId: "",
  setCourseId: () => {},
  modalState: {
    state: CustomCourseworkModulesViewModalState.CLOSED,
    resourceType: null,
    resourceId: null,
    activeModule: null,
    activeActivity: null,
  },
  setModalState: () => {},
  courseData: null,
  courseLoading: false,
  refetchCourse: () => {},
  startPolling: () => {},
  stopPolling: () => {},
  handleSelectModule: () => {},
  handleSelectActivity: () => {},
  handleOpenAddModule: () => {},
  handleOpenGenerateDialog: () => {},
  handleTogglePublishDialog: () => {},
  activeResource: null,
  setActiveResource: () => {},
  assignmentData: null,
  loadingAssignment: false,
  userSubmissionsData: null,
  loadingUserSubmissions: false,
  refetchUserSubmissions: () => {},
  axioResourceData: null,
});

const ActiveCourseProvider = ({ children }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courseId, setCourseId] = useState("");

  const [activeResource, setActiveResource] = useState<{
    resourceType: ResourceType;
    resourceId: string;
  } | null>(null);

  const { dispatch } = useAppState();

  const [modalState, setModalState] = useState<CourseEditPageModalState>({
    state: CustomCourseworkModulesViewModalState.CLOSED,
    resourceType: null,
    resourceId: null,
    activeModule: null,
    activeActivity: null,
  });

  const {
    data: courseData,
    loading: courseLoading,
    refetch: refetchCourse,
    startPolling,
    stopPolling,
  } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
      withModules: true,
    },
    fetchPolicy: "network-only",
    skip: !courseId,
  });

  const { data: assignmentData, loading: loadingAssignment } = useGetAssignmentQuery({
    variables: { id: activeResource?.resourceId },
    skip: activeResource?.resourceType !== ResourceType.ACTIVITY_ASSIGNMENT,
  });

  const {
    data: userSubmissionsData,
    refetch: refetchUserSubmissions,
    loading: loadingUserSubmissions,
  } = useGetUserSubmissionsQuery({
    variables: { assignmentId: activeResource?.resourceId },
    skip: activeResource?.resourceType !== ResourceType.ACTIVITY_ASSIGNMENT,
  });

  const {
    data: axioResourceData,
    loading: loadingAxioResource,
    error: axioResourceError,
  } = useGetAxioResourceQuery({
    variables: { id: activeResource?.resourceId },
    skip: activeResource?.resourceType !== ResourceType.ACTIVITY_RESOURCE,
  });

  useEffect(() => {
    if (courseData?.getCourseById?.data?.[0]?.modules?.length > 0 && !modalState.activeModule) {
      setModalState((prevState) => ({
        ...prevState,
        activeModule: courseData?.getCourseById?.data?.[0]?.modules?.[0],
        activeActivity: null,
      }));
    }
  }, [courseData]);

  useEffect(() => {
    if (!courseData) return;

    const courseGenerationStatus = courseData.getCourseById?.data?.[0]?.generationStatus;

    const isGenerating =
      courseGenerationStatus &&
      courseGenerationStatus !== CourseGenerationStatus.Completed &&
      courseGenerationStatus !== CourseGenerationStatus.Failed &&
      courseGenerationStatus !== CourseGenerationStatus.GeneratingImageCompleted &&
      courseGenerationStatus !== CourseGenerationStatus.GeneratingImageFailed &&
      courseGenerationStatus !== CourseGenerationStatus.GeneratingImage;

    if (isGenerating) {
      startPolling(5000);
      setModalState((prevState) => ({
        ...prevState,
        state: CustomCourseworkModulesViewModalState.COURSE_GENERATION_IN_PROGRESS,
      }));
    } else {
      stopPolling();
      setModalState((prevState) => {
        if (prevState.state === CustomCourseworkModulesViewModalState.COURSE_GENERATION_IN_PROGRESS) {
          return {
            ...prevState,
            state: CustomCourseworkModulesViewModalState.CLOSED,
          };
        }
        return prevState;
      });
    }
  }, [courseData, refetchCourse, startPolling, stopPolling]);

  const handleSelectModule = (module) => {
    setModalState((prevState) => ({
      ...prevState,
      activeModule: module,
      activeActivity: null,
    }));
  };

  const handleSelectActivity = (activity) => {
    setModalState((prevState) => ({
      ...prevState,
      activeActivity: activity,
    }));
  };

  const handleOpenAddModule = () => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setModalState((prevState) => ({
      ...prevState,
      state: CustomCourseworkModulesViewModalState.ADD_MODULE,
      resourceType: ResourceType.MODULE,
      resourceId: null,
    }));
  };

  const handleOpenGenerateDialog = () => {
    setModalState((prevState) => ({
      ...prevState,
      state: CustomCourseworkModulesViewModalState.COURSE_GENERATION_DIALOG,
    }));
  };

  const handleTogglePublishDialog = () => {
    setModalState((prevState) => ({
      ...prevState,
      state: courseData?.getCourseById?.data?.[0]?.isDraft
        ? CustomCourseworkModulesViewModalState.PUBLISH_COURSE
        : CustomCourseworkModulesViewModalState.UNPUBLISH_COURSE,
    }));
  };

  const value = {
    isSubmitting,
    setIsSubmitting,
    courseId,
    setCourseId,
    modalState,
    setModalState,
    courseData,
    courseLoading,
    refetchCourse,
    startPolling,
    stopPolling,
    handleSelectModule,
    handleSelectActivity,
    handleOpenAddModule,
    handleOpenGenerateDialog,
    handleTogglePublishDialog,
    activeResource,
    setActiveResource,
    assignmentData,
    loadingAssignment,
    userSubmissionsData,
    loadingUserSubmissions,
    refetchUserSubmissions,
    axioResourceData,
  };

  return <ActiveCourseContext.Provider value={value}>{children}</ActiveCourseContext.Provider>;
};

const useActiveCourse = () => {
  const context = useContext(ActiveCourseContext);
  if (!context) {
    throw new Error("useActiveCourse must be used within a ActiveCourseProvider");
  }
  return context;
};

export { ActiveCourseProvider, useActiveCourse };
